// Possession — the per-tick puck-on-stick / pickup pipeline.
//
// Owns:   the rule that decides who has the puck right now.
// Reads:  state.puck (pos/vel/mode/ownerId/lastTouchedBy/noRecaptureUntilTick),
//         player.pos / player.stickDir / player.vel / player.isProtecting /
//         player.action / player.possessionQuality, tuning.puck.
// Mutates: state.puck (pos hard-pinned to stick anchor, vel matches owner,
//          mode and ownerId transitions on fumble / pickup),
//          player.hasPuck, player.possessionQuality, player.receivedPassFromTick.
// Emits:  no events directly. Pickup detection sets `receivedPassFromTick`
//         for one-timer eligibility; PossessionChanged is emitted by the
//         strip pathway (`puck-strip.ts`), not here.
//
// Two branches per call:
//   1. Controlled — the puck is on a carrier's stick. Hard-pin pos to
//      `owner.pos + owner.stickDir * controlAnchorDistance`, sync velocity,
//      decay possessionQuality from speed + opponent stick-zone pressure.
//      Drop to Loose if quality < threshold (the "fumble under pressure"
//      mechanic).
//   2. Loose — search all skaters for one whose stick zone covers the
//      puck and is past the recapture lockout. First match in canonical
//      sort order takes the puck.
//
// Called from `core/SimTick.ts` Step 11. Zero-allocation.

import type { PlayerId } from "@arcade-hockey/shared";
import { clampCircleToRink } from "../collision/index.js";
import { clamp01 } from "../math/Geometry.js";
import { tuning } from "../state/tuning.js";
import type { PlayerState, PuckState } from "../state/types.js";
import { isPuckInStickZone } from "./stick.js";

export type PossessionContext = {
  players: PlayerState[];
  /**
   * Hot-path callers (tickMatch) supply the cache so the controlled-puck
   * branch is O(1); tests may omit it and pay the per-call find.
   */
  playersById?: Map<PlayerId, PlayerState>;
  puck: PuckState;
  currentTick: number;
};

function lookup(
  players: PlayerState[],
  playersById: Map<PlayerId, PlayerState> | undefined,
  id: PlayerId,
): PlayerState | undefined {
  if (playersById !== undefined) return playersById.get(id);
  return players.find((p) => p.id === id);
}

export function tickPossession(ctx: PossessionContext): void {
  const { puck, players, playersById, currentTick } = ctx;

  if (puck.mode === "Controlled" && puck.ownerId !== null) {
    const owner = lookup(players, playersById, puck.ownerId);
    if (owner === undefined) {
      // Owner gone — drop.
      puck.mode = "Loose";
      puck.ownerId = null;
      return;
    }

    // Hard-pin the puck to the stick blade (Slice 16). The carrier moves
    // once per tick under applySkating; the puck rides along by deriving
    // its position from owner.pos + stickDir each tick. No spring lag → the
    // puck never appears inside the body. Velocity matches the owner's so
    // the transition to Loose / Shot / Pass inherits sensible momentum.
    const anchorDist = tuning.puck.controlAnchorDistance;
    puck.pos.x = owner.pos.x + owner.stickDir.x * anchorDist;
    puck.pos.y = owner.pos.y + owner.stickDir.y * anchorDist;
    puck.vel.x = owner.vel.x;
    puck.vel.y = owner.vel.y;
    // Keep the controlled puck inside the rink — the carrier is already
    // clamped, but the 0.9 m stick anchor reaches past the boards along the
    // straights and outside the rounded corners.
    clampCircleToRink(puck.pos, tuning.puck.radius);

    // Possession quality update. High-speed skating still decays possession
    // (the "you fumble when you sprint" feel). Slice 16 adds an opponent-
    // pressure penalty: any opposing skater whose stick zone covers the
    // pinned puck decays the carrier's quality faster. The old soft-spring
    // implicitly broke up dogpiles by drifting the puck past the drop
    // distance under shoves; with hard-pin the puck can't drift, so a
    // pressure-driven decay restores the same outcome (carrier eventually
    // loses possession under sustained defensive coverage). Stunned /
    // recovering opponents don't count; protect-puck is immune to the
    // pressure penalty by Slice 8 convention.
    const ownerVelLen = Math.sqrt(owner.vel.x * owner.vel.x + owner.vel.y * owner.vel.y);
    const speedPenalty = (ownerVelLen / tuning.player.maxSpeed) * 0.02;
    let pressurePenalty = 0;
    if (!owner.isProtecting) {
      for (const other of players) {
        if (other.id === owner.id) continue;
        if (other.benched) continue;
        if (other.teamId === owner.teamId) continue;
        if (other.action === "Stunned" || other.action === "RecoverCheck") continue;
        if (isPuckInStickZone(other, puck.pos)) {
          pressurePenalty = 0.02;
          break;
        }
      }
    }
    owner.possessionQuality = clamp01(
      owner.possessionQuality + 0.015 - speedPenalty - pressurePenalty,
    );

    if (owner.possessionQuality < tuning.puck.possessionMinQuality) {
      puck.mode = "Loose";
      puck.ownerId = null;
      owner.hasPuck = false;
      owner.possessionQuality = 0;
      puck.lastTouchedBy = owner.id;
      puck.noRecaptureUntilTick = currentTick + tuning.puck.noRecaptureTicks;
    }
    return;
  }

  // Loose puck: search for a player in stick zone. `players` is in canonical
  // sorted order (see createMatch); do NOT re-sort per tick.
  if (puck.mode === "Loose") {
    for (const p of players) {
      // Slice 8 — benched skaters (OT wings) can't claim the puck.
      if (p.benched) continue;
      // Faceoff centre-lockout: both centres are tied up in the draw
      // for ~0.5s after a faceoff pop so the puck reaches the
      // targeted teammate instead.
      if (p.position === "C" && currentTick < puck.faceoffCenterLockoutUntilTick) continue;
      if (currentTick < puck.noRecaptureUntilTick && p.id === puck.lastTouchedBy) continue;
      // Phase H Slice 4 — same-opponent lockout. If this player was
      // the one who just lost the puck to an opponent, they're blocked
      // from re-claiming for `sameOpponentRecaptureTicks` ticks.
      if (currentTick < puck.opponentLockoutUntilTick && p.id === puck.opponentLockoutPlayerId) {
        continue;
      }
      if (!isPuckInStickZone(p, puck.pos)) continue;

      // Detect pass reception: previous toucher was a teammate.
      const previousToucher = puck.lastTouchedBy;
      if (previousToucher !== null && previousToucher !== p.id) {
        const previousTouchPlayer = lookup(players, playersById, previousToucher);
        if (previousTouchPlayer !== undefined && previousTouchPlayer.teamId === p.teamId) {
          p.receivedPassFromTick = currentTick;
        }
      }

      // Phase H Slice 4 — set the opponent lockout when an opponent
      // takes possession. The previous owner (lastTouchedBy, if from
      // the opposing team) is the one we lock out.
      if (previousToucher !== null && previousToucher !== p.id) {
        const previousTouchPlayer = lookup(players, playersById, previousToucher);
        if (previousTouchPlayer !== undefined && previousTouchPlayer.teamId !== p.teamId) {
          puck.opponentLockoutPlayerId = previousToucher;
          puck.opponentLockoutUntilTick = currentTick + tuning.puck.sameOpponentRecaptureTicks;
        }
      }

      // Claim.
      puck.mode = "Controlled";
      puck.ownerId = p.id;
      puck.lastTouchedBy = p.id;
      p.hasPuck = true;
      p.possessionQuality = 0.6;
      // Pin the puck to the stick anchor on the same tick as the claim
      // (Slice 16). Without this, the puck stays at its last-loose position
      // for one tick before the next Controlled-branch run snaps it to the
      // stick — visible as a one-frame jitter at every pickup.
      const anchorDist = tuning.puck.controlAnchorDistance;
      puck.pos.x = p.pos.x + p.stickDir.x * anchorDist;
      puck.pos.y = p.pos.y + p.stickDir.y * anchorDist;
      puck.vel.x = p.vel.x;
      puck.vel.y = p.vel.y;
      clampCircleToRink(puck.pos, tuning.puck.radius);
      return;
    }
  }
}
